import { useCallback, useMemo } from "react";
import { type MinimalQueryResult } from "@jsq/ds/types";
import { useProfileComparisonQuery } from "~/graphql/supergraphHooks";
import { useCountryOptions } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/AddressFields/CountryFormField/useCountryOptions";
import { isPresent } from "../CompareTable/utils";
import {
  buildAssociatedProfileMatchData,
  buildNewProfileMatchData,
  buildUnassociatedProfileMatchData,
} from "./utils";
import type { ProfileMatchData } from "./types";
import type { CountryNameResolver } from "./utils";
import type { WizardProfileData } from "../../types";
import type { InvestmentAccountNode } from "../CompareTable/utils";

export const useProfileComparison = ({
  arenaId,
  entityId,
  investmentAccountId,
  investmentAccountSettled,
  profileId,
  profileName,
  isIaAssociated = false,
  newProfile,
  clientName = null,
}: {
  arenaId: number | undefined;
  entityId: number | undefined;
  investmentAccountId: number | null;
  /** False while the case's investment account is still resolving, when a null id means "unknown". */
  investmentAccountSettled: boolean;
  profileId?: number | null;
  /** Name of the selected existing profile, used to search when it is not IA-associated. */
  profileName?: string | null;
  isIaAssociated?: boolean;
  /** Set instead of `profileId` when comparing a not-yet-created profile from the wizard form. */
  newProfile?: WizardProfileData | null;
  /** The case's own client, used for a new profile's client row since it has no client of its own. */
  clientName?: string | null;
}): {
  matchData: ProfileMatchData | null;
  queryResult: MinimalQueryResult;
  investmentAccountNode: InvestmentAccountNode | null;
} => {
  const { countryOptions } = useCountryOptions();
  const resolveCountryName = useCallback<CountryNameResolver>(
    (code) => countryOptions.find((option) => option.code === code)?.name ?? code ?? null,
    [countryOptions],
  );

  const isNewProfile = profileId == null && newProfile != null;
  const iaKnown = investmentAccountId !== null;
  // Skip until the account settles, so the by-name fallback can't race the account-scoped query.
  const baseSkip = arenaId === undefined || entityId === undefined || !investmentAccountSettled;

  // The IA-scoped query returns the investment account plus its associated profiles, including the
  // server-computed match; the by-name fallback yields neither an account nor a server match.
  const useIaQuery = iaKnown && (isNewProfile || (profileId != null && isIaAssociated));
  const useNameQuery =
    !isNewProfile && profileId != null && !useIaQuery && isPresent(profileName);
  const skip = baseSkip || (!useIaQuery && !useNameQuery);

  const queryResult = useProfileComparisonQuery({
    variables: {
      arenaId: arenaId ?? 0,
      entityId: entityId ?? 0,
      investmentAccountId: useIaQuery ? (investmentAccountId ?? undefined) : undefined,
      name: useNameQuery ? profileName : undefined,
    },
    skip,
    // `matchPercent` and `perFieldMatch` are computed against the investment account in the
    // variables, but the cache normalizes profiles by id alone. Sharing that entity lets a
    // match-less response for the same profile overwrite this one's comparison.
    fetchPolicy: "no-cache",
  });

  const investmentAccountNode = useMemo<InvestmentAccountNode | null>(
    () => (useIaQuery ? (queryResult.data?.rankedProfile?.investmentAccount ?? null) : null),
    [queryResult.data, useIaQuery],
  );

  const matchData = useMemo<ProfileMatchData | null>(() => {
    if (isNewProfile) {
      return newProfile
        ? buildNewProfileMatchData(
            newProfile,
            investmentAccountNode,
            resolveCountryName,
            clientName,
          )
        : null;
    }

    const profileNode =
      queryResult.data?.rankedProfile?.profiles.find(
        (entry) => Number(entry.id) === profileId,
      ) ?? null;
    if (!profileNode) return null;

    // Only a profile fetched against this investment account carries a server-computed match to
    // trust; one found by name instead has no comparison and shows only its own data.
    return useIaQuery
      ? buildAssociatedProfileMatchData(profileNode, investmentAccountNode, profileId ?? null)
      : // Only one client is supported per profile today, so the 0th entry is safe to read.
        buildUnassociatedProfileMatchData(
          profileNode,
          profileNode.clientObjects?.[0]?.name ?? null,
        );
  }, [
    queryResult.data,
    profileId,
    isNewProfile,
    newProfile,
    useIaQuery,
    investmentAccountNode,
    resolveCountryName,
    clientName,
  ]);

  // A comparison that is waiting on the account is pending, not unavailable, so the caller keeps
  // its loader instead of flashing the empty state while the query is still skipped.
  const pendingAwareResult = useMemo<MinimalQueryResult>(
    () => ({ ...queryResult, loading: queryResult.loading || !investmentAccountSettled }),
    [queryResult, investmentAccountSettled],
  );

  return { matchData, queryResult: pendingAwareResult, investmentAccountNode };
};
